import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Save } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyDay = () => ({
  enabled: false, start_time: "09:00", end_time: "17:00",
  break_start: "", break_end: "",
});

export default function Schedules() {
  const [doctors, setDoctors] = useState([]);
  const [docId, setDocId] = useState("");
  const [days, setDays] = useState(Array.from({ length: 7 }, emptyDay));
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newBlockDate, setNewBlockDate] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");

  useEffect(() => {
    api.get("/hospital/doctors")
      .then((r) => {
        const active = r.data;
        setDoctors(active);
        if (active.length && !docId) setDocId(active[0].id);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!docId) return;
    api.get(`/hospital/doctors/${docId}/schedule`).then((r) => {
      const next = Array.from({ length: 7 }, emptyDay);
      for (const w of r.data.weekly || []) {
        next[w.weekday] = {
          enabled: true,
          start_time: w.start_time, end_time: w.end_time,
          break_start: w.break_start || "", break_end: w.break_end || "",
        };
      }
      setDays(next);
      setBlocks(r.data.blocks || []);
    });
  }, [docId]);

  const updateDay = (i, patch) =>
    setDays((arr) => arr.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  const save = async () => {
    setSaving(true);
    try {
      const rows = days
        .map((d, i) => d.enabled ? {
          weekday: i,
          start_time: d.start_time,
          end_time: d.end_time,
          break_start: d.break_start || null,
          break_end: d.break_end || null,
        } : null)
        .filter(Boolean);
      await api.put(`/hospital/doctors/${docId}/schedule`, { rows });
      toast.success("Schedule saved");
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    if (!newBlockDate) return;
    try {
      const { data } = await api.post(`/hospital/doctors/${docId}/blocks`, {
        date: newBlockDate, reason: newBlockReason || null,
      });
      setBlocks((b) => [...b, data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewBlockDate(""); setNewBlockReason("");
      toast.success("Block added");
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  const removeBlock = async (id) => {
    try {
      await api.delete(`/hospital/doctors/${docId}/blocks/${id}`);
      setBlocks((b) => b.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  const selectedDoctor = doctors.find((d) => d.id === docId);

  return (
    <div data-testid="schedules-page" className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ schedules</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Weekly availability</h1>
        <p className="text-sm text-mb-fg2 mt-1">Slot duration is set per doctor; bookings open in Phase 3.</p>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface p-5">
        <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Doctor</Label>
        <div className="mt-2 flex flex-col sm:flex-row gap-3 sm:items-center">
          <Select value={docId} onValueChange={setDocId}>
            <SelectTrigger data-testid="schedule-doctor-select"
              className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg sm:w-96">
              <SelectValue placeholder={loading ? "Loading…" : "Select doctor"} />
            </SelectTrigger>
            <SelectContent className="bg-mb-surface2 border-mb-border text-white max-h-72">
              {doctors.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.full_name} — {d.specialty} {!d.is_active && "(inactive)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDoctor && (
            <div className="text-xs text-mb-fg2">
              Slot: <span className="text-mb-fg2">{selectedDoctor.slot_duration_minutes} min</span> · Fee:{" "}
              <span className="text-mb-fg2">{formatCurrency(selectedDoctor.consultation_fee)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Weekly grid */}
      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden" data-testid="weekly-grid">
        <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between">
          <div className="text-sm font-medium">Weekly template</div>
          <Button onClick={save} disabled={saving || !docId} data-testid="save-schedule"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg h-9 px-4 font-medium">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save schedule"}
          </Button>
        </div>
        <ul className="divide-y divide-mb-border">
          {DAYS.map((label, i) => {
            const d = days[i];
            return (
              <li key={i} data-testid={`day-row-${i}`}
                className="px-6 py-4 grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3 md:items-center">
                <div className="flex items-center gap-3">
                  <Switch checked={d.enabled} onCheckedChange={(v) => updateDay(i, { enabled: v })}
                    data-testid={`day-toggle-${i}`}
                    className="data-[state=checked]:bg-teal-500" />
                  <div className="text-sm font-medium">{label}</div>
                </div>
                {d.enabled ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Time label="Start" value={d.start_time} onChange={(v) => updateDay(i, { start_time: v })} testid={`day-start-${i}`} />
                    <Time label="End" value={d.end_time} onChange={(v) => updateDay(i, { end_time: v })} testid={`day-end-${i}`} />
                    <Time label="Break start" value={d.break_start} onChange={(v) => updateDay(i, { break_start: v })} testid={`day-break-start-${i}`} optional />
                    <Time label="Break end" value={d.break_end} onChange={(v) => updateDay(i, { break_end: v })} testid={`day-break-end-${i}`} optional />
                  </div>
                ) : (
                  <div className="text-xs text-mb-fg2">Off</div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Blocked dates */}
      <div className="rounded-xl border border-mb-border bg-mb-surface" data-testid="blocks-card">
        <div className="px-6 py-4 border-b border-mb-border text-sm font-medium">Blocked dates</div>
        <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 sm:items-end border-b border-mb-border">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Date</Label>
            <Input type="date" value={newBlockDate} onChange={(e) => setNewBlockDate(e.target.value)}
              data-testid="block-date"
              className="mt-2 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg focus-visible:ring-teal-500" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Reason (optional)</Label>
            <Input value={newBlockReason} onChange={(e) => setNewBlockReason(e.target.value)}
              data-testid="block-reason"
              className="mt-2 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg focus-visible:ring-teal-500" />
          </div>
          <Button onClick={addBlock} disabled={!newBlockDate || !docId} data-testid="add-block"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg h-10 px-4 font-medium">
            <Plus className="h-4 w-4 mr-2" /> Add block
          </Button>
        </div>
        <ul className="divide-y divide-mb-border">
          {blocks.length === 0 && (
            <li className="px-6 py-6 text-sm text-mb-fg2 text-center">No blocked dates.</li>
          )}
          {blocks.map((b) => (
            <li key={b.id} data-testid={`block-row-${b.id}`}
              className="px-6 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm text-white">{b.date}</div>
                <div className="text-xs text-mb-fg2">{b.reason || "—"}</div>
              </div>
              <Button onClick={() => removeBlock(b.id)} variant="ghost"
                data-testid={`remove-block-${b.id}`}
                className="h-9 hover:bg-red-500/10 text-red-400">
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Time({ label, value, onChange, testid, optional }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}{optional && <span className="text-zinc-700"> · opt</span>}</Label>
      <Input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg focus-visible:ring-teal-500" />
    </div>
  );
}
